// The moment one is found (ACHIEVEMENTS.md §12; BACKLOG A4; scenarios R1, R2
// and R7): a card, not a toast.
//
// It slides in with the badge popping into color — "Achievement unlocked", or
// "Secret found!" for a hidden one — then the name and the line. Tappable: it
// opens the wall at the badge. It plays the unlock sound when sounds are on,
// and goes after a few seconds (not while a pointer or focus is on it).
//
// NEVER OVER A PICK OR A SHEET. It waits while the person is on the clock in a
// draft (`useHoldMoments`) or any sheet or dialog is open, and while the page
// is not in front of them, then shows what has gathered. Nor beside a
// milestone's card: `Moments.tsx` gives the screen to one at a time.
//
// TWO OR MORE AT ONCE ARE ONE CARD ("3 achievements"), which opens the wall at
// what was just found; a backfill's finds are "New achievements arrived, and
// 4 are already yours" (§13). Never a stack of pop-ups.
//
// ONCE ON ANY DEVICE: shown here, it is marked seen (`markSeen`), so the phone
// does not show what the laptop already did.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FoundAchievement } from '@fsp/domain';
import {
  markSeen,
  momentsClear,
  takeMoments,
  usePendingMoments,
  type MomentTurn,
} from '../data/achievements';
import { useAtLeast } from '../data/detail';
import { useSession } from '../data/session';
import { useDelight } from '../delight/useDelight';
import { AchievementGlyph } from '../kit/Icon';
import { IconButton } from '../kit/ui';
import { hueOf } from '../screens/shared/AchievementKit';
import { useTier } from './useTier';
import '../styles/screens/achievements.css';

const SHOW_MS = 6500;
const LOOK_EVERY_MS = 700;

const hidden = (a: FoundAchievement) => a.shows !== 'open';

/** What the card says for what it holds. */
function wordsFor(
  batch: readonly FoundAchievement[],
  counts: boolean,
): { eyebrow: string; name: string; line: string } {
  const [first] = batch;
  if (batch.length === 1 && first !== undefined)
    return {
      eyebrow: first.retro
        ? 'Found in what already happened'
        : hidden(first)
          ? 'Secret found!'
          : 'Achievement unlocked',
      name: first.name,
      line: first.line,
    };
  const names = batch.slice(0, 3).map((a) => a.name);
  const listed =
    batch.length > 3
      ? `${names.join(', ')} and more`
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  // SIMPLE REMOVES THE NUMBER AND ADDS NO WORD (DETAIL_LEVELS.md): the
  // count's clause goes, rather than becoming "some".
  if (batch.every((a) => a.retro))
    return {
      eyebrow: 'New achievements',
      name: counts
        ? `New achievements arrived, and ${batch.length} are already yours`
        : 'New achievements arrived',
      line: listed,
    };
  return {
    eyebrow: batch.some(hidden) ? 'Secrets found!' : 'Achievements unlocked',
    name: counts ? `${batch.length} achievements` : 'Achievements',
    line: listed,
  };
}

export function AchievementMoment({ mayShow, onUp }: MomentTurn) {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const pending = usePendingMoments(userId);
  const [showing, setShowing] = useState<FoundAchievement[] | null>(null);
  const [held, setHeld] = useState(false);
  const counts = useAtLeast('standard');
  const tier = useTier();
  const navigate = useNavigate();
  const { play } = useDelight();
  const playRef = useRef(play);
  playRef.current = play;
  const close = useCallback(() => {
    onUp(false);
    setShowing(null);
  }, [onUp]);

  // WAIT FOR A CLEAR SCREEN, then show everything that has gathered.
  useEffect(() => {
    if (userId === null || showing !== null || pending.length === 0) return;
    const tryShow = (): boolean => {
      if (!momentsClear() || !mayShow()) return false;
      const batch = takeMoments(userId);
      if (batch.length === 0) return true;
      onUp(true);
      setShowing(batch);
      playRef.current('unlock');
      void markSeen(batch.map((a) => a.id));
      return true;
    };
    if (tryShow()) return;
    const timer = window.setInterval(() => {
      if (tryShow()) window.clearInterval(timer);
    }, LOOK_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [mayShow, onUp, userId, pending, showing]);

  // A CARD THAT GOES TAKES ITS HOLD WITH IT: a tap or Dismiss removes it
  // under the pointer, so no leave or blur ever arrives to let go (the review
  // of A4), and the next card would stay up until dismissed by hand.
  useEffect(() => {
    if (showing === null) setHeld(false);
  }, [showing]);

  // GONE AFTER A FEW SECONDS, unless a pointer or focus is on it.
  useEffect(() => {
    if (showing === null || held) return;
    const timer = window.setTimeout(close, SHOW_MS);
    return () => window.clearTimeout(timer);
  }, [showing, held, close]);

  // Signed out mid-card: it was theirs, not the next person's.
  useEffect(() => {
    if (userId === null) close();
  }, [userId, close]);

  if (showing === null || showing.length === 0) return null;
  const first = showing[0]!;
  const words = wordsFor(showing, counts);
  const to =
    showing.length === 1
      ? `/account/achievements?badge=${encodeURIComponent(first.id)}`
      : '/account/achievements?show=found';

  return (
    <div
      className="fsp-moment fsp-ach-hued"
      data-hue={hueOf(first.family)}
      data-tier={tier}
      role="status"
      aria-live="polite"
      onPointerEnter={() => setHeld(true)}
      onPointerLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <button
        type="button"
        className="fsp-moment-open"
        onClick={() => {
          close();
          navigate(to);
        }}
      >
        <span className="fsp-ach-glyph">
          <AchievementGlyph icon={showing.length === 1 ? first.icon : 'medal'} size={24} />
        </span>
        <span className="fsp-grow">
          <span className="fsp-moment-eyebrow">{words.eyebrow}</span>
          <span className="fsp-moment-name">{words.name}</span>
          <span className="fsp-moment-line">{words.line}</span>
        </span>
      </button>
      <IconButton icon="close" label="Dismiss" size="sm" variant="quiet" onClick={() => close()} />
    </div>
  );
}
